import { useEditor } from '@candulabs/craft';
import {
  ButtonIcon,
  Callout,
  css,
  EditIcon,
  EllipsisIcon,
  ListItem,
  Paragraph,
  PlusIcon,
  SelectInput,
  styled,
  TrashIcon,
} from 'canoe';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { NewListItem } from 'canoe/components/ListItem';
import { useToggle } from 'commons/hooks';
import { OptionModalConditional } from 'craft/resolvers/ConditionalView/components/OptionModalConditional';
import { useFlow, useFlowDelete, useViewportDom } from 'craft/state';
import { createEveryoneConditionalFlowStep } from 'craft/state/flows/createConditionalFlowStep';
import { OptionsWrapper } from 'styleguide/components/Input/Select';

const TOPBAR_HEIGHT = 40;

const InputWrapper = styled.div<{ displayMode: 'normal' | 'compact' }>`
  ${({ displayMode }) => css`
    flex-grow: 1;
    max-width: ${displayMode === 'normal' ? '250px' : 'none'};
  `}
`;

const Container = styled.div<{ ref: any }>`
  ${({ theme }) => css`
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;

    padding: 8px;
    height: ${TOPBAR_HEIGHT}px;

    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    z-index: 100;

    &:after {
      content: '';

      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;

      background-color: ${theme.colors.BrandPurpleL10};
      width: 100%;
      height: ${TOPBAR_HEIGHT}px;
    }

    ${OptionsWrapper} {
      font-size: 0.75em;
    }
  `}
`;

const CalloutWrapper = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spaces.xs};
    background-color: ${theme.colors.White};
    border: 1px solid ${theme.colors.BrandPurpleL10};
    border-radius: ${theme.borderRadius.small};
    position: relative;
    z-index: 10001;
    width: 300px;
    white-space: initial;
    display: flex;
    flex-direction: column;
    gap: ${theme.spaces.xs};
    align-items: stretch;
  `}
`;

const AddContainer = styled(Paragraph)`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    justify-content: center;
    font-size: ${theme.fonts.small.fontSize};
    padding: 0 ${theme.spaces.xs};
    cursor: pointer;
  `}
`;

interface FloatingHeaderProps {
  id: string;
}

export const FloatingHeader = ({ id }: FloatingHeaderProps) => {
  const { query } = useEditor();
  const { index: activeIndex, onSetStepId, stepIds, onAddStep, stepNames } = useFlow(id);
  const { onDeleteStepId, canDeleteSteps } = useFlowDelete(id);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contextButtonRef = useRef<HTMLButtonElement>(null);
  const { viewportWrapperEl } = useViewportDom();

  const latestDisplayMode = useRef<'normal' | 'compact'>('compact');
  const [displayMode, setDisplayMode] = useState<'normal' | 'compact'>('compact');
  const [calloutOpen, toggleCallout] = useToggle(false);
  const [isConditionalModalOpen, toggleConditionalModal] = useToggle(false);
  const [isEditingIndex, setIsEditingIndex] = useState<number | null>(null);

  /**
   * Recalculate the number of visible tabs based on the available width
   */
  const recalculateDisplayMode = useCallback(() => {
    if (!wrapperRef.current) {
      return;
    }

    const totalWidth = wrapperRef.current.clientWidth;

    if (totalWidth <= 600) {
      // compact mode
      if (latestDisplayMode.current !== 'compact') {
        latestDisplayMode.current = 'compact';
        setDisplayMode('compact');
      }
    } else if (latestDisplayMode.current !== 'normal') {
      // normal mode
      latestDisplayMode.current = 'normal';
      setDisplayMode('normal');
    }
  }, []);

  const throttleRecalculateDisplayMode = useCallback(
    throttle(() => {
      recalculateDisplayMode();
    }, 250),
    [recalculateDisplayMode],
  );

  /**
   * When the header resizes, recalculate how it should display
   */
  useEffect(() => {
    if (!wrapperRef.current) {
      return () => null;
    }

    // make sure to recalculate nav arrows when the viewportContainer is resized
    const observer = new ResizeObserver(throttleRecalculateDisplayMode);
    observer.observe(wrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, [throttleRecalculateDisplayMode]);

  useEffect(() => {
    if (displayMode !== 'compact' && calloutOpen) {
      toggleCallout();
    }

    if (latestDisplayMode.current !== displayMode) {
      // if these get out of sync, usually due to a new render, make sure they are in sync
      latestDisplayMode.current = displayMode;
    }
  }, [displayMode, calloutOpen]);

  const conditionalOptionNodeData = useMemo(
    () => stepIds.map((optionId) => query.node(optionId).get().data),
    [stepIds],
  );

  const activeOption = useMemo(() => conditionalOptionNodeData[activeIndex], [
    activeIndex,
    conditionalOptionNodeData,
  ]);

  const settings = useMemo(
    () => (
      <>
        <InputWrapper displayMode={displayMode}>
          {/* TODO: Add options for edit/delete - when editing setIsEditingIndex(index) before triggering conditional modal */}
          <SelectInput<{ value: string; label: string; addType?: string }>
            clearable={false}
            onSelect={(selectedItem) => selectedItem && onSetStepId(selectedItem.value)}
            selectedItem={{
              label: activeOption.props.name,
              value: stepIds[activeIndex],
            }}
            items={[
              ...conditionalOptionNodeData.map((option, index) => ({
                label: option.props.name,
                value: stepIds[index],
              })),
              ...(!stepNames.find((n) => n === 'Everyone')
                ? [
                    {
                      label: 'Add Everyone View',
                      value: 'Everyone',
                      addType: 'everyone',
                    },
                  ]
                : []),
              {
                label: 'Add New View',
                value: 'New',
                addType: 'new',
              },
            ]}
            placeholder="Select Option"
            scale="small"
            data-craft-ignore-events
            usePortal={displayMode === 'normal'}
            renderItem={({ item, ...listItemProps }) => {
              if (item.addType) {
                return (
                  <NewListItem
                    scale={listItemProps.scale}
                    onClick={() => {
                      listItemProps.onCloseList?.();

                      if (item.addType === 'new') {
                        toggleConditionalModal();
                      } else {
                        onAddStep(createEveryoneConditionalFlowStep());
                      }
                    }}
                  >
                    <AddContainer color="OnyxGrayL30" mb="none">
                      <PlusIcon color="OnyxGrayL30" /> {item.label}
                    </AddContainer>
                  </NewListItem>
                );
              }

              return (
                <ListItem
                  key={item.value}
                  isSelected={item.value === stepIds[activeIndex]}
                  {...listItemProps}
                  suffix={
                    <>
                      {item.label !== 'Everyone' && (
                        <ButtonIcon
                          icon={EditIcon}
                          size="small"
                          onClick={() => {
                            setIsEditingIndex(stepIds.findIndex((sid) => sid === item.value));
                            toggleConditionalModal();
                          }}
                        />
                      )}
                      {canDeleteSteps && (
                        <ButtonIcon
                          icon={TrashIcon}
                          size="small"
                          onClick={() => onDeleteStepId(item.value)}
                        />
                      )}
                    </>
                  }
                >
                  {item.label}
                </ListItem>
              );
            }}
          />
        </InputWrapper>
      </>
    ),
    [
      displayMode,
      activeOption,
      stepIds,
      activeIndex,
      conditionalOptionNodeData,
      onAddStep,
      onDeleteStepId,
      canDeleteSteps,
    ],
  );

  if (displayMode === 'compact') {
    return (
      <Container ref={wrapperRef}>
        <ButtonIcon
          ref={contextButtonRef}
          icon={EllipsisIcon}
          size="s"
          radius="small"
          color="white"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            toggleCallout();
          }}
          data-craft-ignore-events
        />

        {calloutOpen && (
          <Callout
            referenceElement={contextButtonRef.current}
            onClose={toggleCallout}
            modifiers={[
              {
                name: 'preventOverflow',
                options: {
                  boundary: viewportWrapperEl,
                  padding: 8,
                },
              },
            ]}
          >
            {({ ref, style, attributes }) => (
              <CalloutWrapper data-craft-ignore-events ref={ref} style={style} {...attributes}>
                {settings}
              </CalloutWrapper>
            )}
          </Callout>
        )}
        {isConditionalModalOpen && (
          <OptionModalConditional
            nodeId={id}
            isEditingIndex={isEditingIndex}
            onClose={() => {
              toggleConditionalModal();
              setIsEditingIndex(null);
            }}
          />
        )}
      </Container>
    );
  }

  return (
    <Container ref={wrapperRef} data-craft-ignore-events>
      {settings}
      {isConditionalModalOpen && (
        <OptionModalConditional
          nodeId={id}
          isEditingIndex={isEditingIndex}
          onClose={() => {
            toggleConditionalModal();
            setIsEditingIndex(null);
          }}
        />
      )}
    </Container>
  );
};
